import {
  Box,
  HeadingText,
  StackLayout,
  useMediaQuery,
  queryHelpers,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { FormattedMessage, useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { usePersonalInfo } from 'pages/personal-info/use-personal-info.hook';
import { MaskedValue } from 'pages/personal-info/masked-value.component';
import { renderPersonalInfoLink } from 'common/links';
import { OnboardingPageWrapper } from 'components/onboarding-page-wrapper/onboarding-page-wrapper.component';

const CAREGIVER_FIELD_IDS = new Set([
  'STR_PREFERRED_NAME',
  'STR_FIRST_NAME',
  'STR_LAST_NAME',
  'STR_EMAIL_ADDRESS',
]);

export const AccountSetupPage = ({
  onComplete,
  isCaregiver = false,
}: {
  onComplete: () => void;
  isCaregiver?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  useDocumentTitle(formatMessage({ id: 'STR_WELCOME' }));

  const { fields, isLoading, isError, refetch } = usePersonalInfo();

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isError) {
    return <ConnectionErrorPage retry={refetch} />;
  }

  const firstNameValue =
    fields.find((f) => f.labelId === 'STR_FIRST_NAME')?.value ?? '';
  const lastNameValue =
    fields.find((f) => f.labelId === 'STR_LAST_NAME')?.value ?? '';

  // TODO: consider using the avatar image from userProfile as the primary source,
  // falling back to initials, then '?' when no name is available.
  const initials = [firstNameValue[0], lastNameValue[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();

  const fullName = [firstNameValue, lastNameValue].filter(Boolean).join(' ');

  const visibleFields = isCaregiver
    ? fields.filter((f) => CAREGIVER_FIELD_IDS.has(f.labelId))
    : fields;

  const heading = formatMessage({ id: 'STR_WELCOME' });

  return (
    <OnboardingPageWrapper onButtonClick={onComplete}>
      <StackLayout
        orientation="vertical"
        horizontalAlignment="center"
        css={{
          padding: isMobile ? '$twoAndHalf $twoAndHalf $five' : '$twoAndHalf',
          gap: '$oneAndHalf',
        }}
      >
        <HeadingText level="1" size="xl" css={{ textAlign: 'center' }}>
          {heading}
        </HeadingText>

        <StackLayout
          css={{
            gap: '$half',
            alignItems: 'center',
            marginBottom: '$oneAndHalf',
          }}
        >
          <Box
            aria-hidden="true"
            css={{
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              backgroundColor: '$decorativeBrandPrimaryDarkest',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HeadingText
              as="div"
              level="1"
              size="xxl"
              css={{ color: '$onSurfaceTextReversed' }}
            >
              {initials || '?'}
            </HeadingText>
          </Box>

          {fullName && (
            <HeadingText as="h2" level="2" size="lg">
              {fullName}
            </HeadingText>
          )}
        </StackLayout>

        <UtilityText
          css={{
            typography: '$bodyTwo',
            textAlign: 'start',
            alignSelf: 'stretch',
          }}
        >
          <FormattedMessage
            id="STR_ACCOUNT_SETUP_MYCHART_INFO"
            values={{
              myChartLink: renderPersonalInfoLink('$subtitleTwo'),
            }}
          />
        </UtilityText>

        <Box
          css={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '$one',
          }}
        >
          {visibleFields.map(({ labelId, value, forceLtr, sensitive }) => (
            <Box
              key={labelId}
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: sensitive ? '$none' : '$half',
              }}
            >
              <UtilityText
                css={{
                  typography: '$subtitleTwo',
                  fontWeight: 'bold',
                  textAlign: 'start',
                }}
              >
                {formatMessage({ id: labelId })}
              </UtilityText>
              {sensitive ? (
                <MaskedValue
                  value={value as string}
                  dir={forceLtr ? 'ltr' : undefined}
                  css={{ color: '$onSurfaceTextSubdued' }}
                />
              ) : (
                <UtilityText
                  dir={forceLtr ? 'ltr' : undefined}
                  css={{ color: '$onSurfaceTextSubdued' }}
                >
                  {value}
                </UtilityText>
              )}
            </Box>
          ))}
        </Box>
      </StackLayout>
    </OnboardingPageWrapper>
  );
};
